// Панель сравнения вариантов: строки — метрики, колонки — варианты.
// Отсюда же поездкой делятся: ссылка отдаёт наружу именно этот итог.
import { useId, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useStore } from '../../store';
import { useT } from '../../i18n';
import { computeVariantTotals } from '../../core';
import type { VariantTotals } from '../../types';
import { ShareButton } from '../../share';
import { useIsMobile } from '../shell';
// Аккордеон мобильных карточек — общая мелочь с библиотекой (D11), см.
// комментарий у экспорта в ui/library/index.ts.
import { loadAccordionOpen, saveAccordionOpen } from '../library';
import { COMPARE_METRICS, computeBestSet, maxWarningLevel } from './metrics';
import styles from './ComparePanel.module.css';

function warningColorVar(level: 'error' | 'warn' | null): string {
  if (level === 'error') return 'var(--error)';
  if (level === 'warn') return 'var(--warn)';
  return 'var(--text-3)';
}

export function ComparePanel(): ReactElement {
  const state = useStore();
  const t = useT();

  const totals: VariantTotals[] = useMemo(
    () => state.variants.map((v) => computeVariantTotals(v, state, t)),
    [state, t],
  );

  // D11: тот же аккордеон, что у библиотеки, но дефолт свой — открыта. Ради
  // сравнения вариантов на телефон и заходят, а свёрнутая карточка показывала
  // одну строку заголовка со счётчиком: итог приходилось раскрывать вручную
  // при каждом визите. Раньше дефолт считался от количества вариантов
  // (открыта, только пока их ноль) — то есть закрывалась ровно тогда, когда в
  // ней появлялось что сравнивать. Ручной выбор по-прежнему сильнее дефолта и
  // помнится сессию (mobileAccordion.ts). На десктопе ветка ниже вообще не
  // задействуется: там всегда рендерится обычный <h2>.
  const isMobile = useIsMobile();
  const tableId = useId();
  const [accordionOpen, setAccordionOpen] = useState(() => loadAccordionOpen('compare', true));
  const showBody = !isMobile || accordionOpen;
  function toggleAccordion() {
    setAccordionOpen((prev) => {
      const next = !prev;
      saveAccordionOpen('compare', next);
      return next;
    });
  }

  return (
    <aside className={styles.panel} aria-label={t('compare.title')}>
      {isMobile ? (
        <button
          type="button"
          className={styles.accordionHead}
          aria-expanded={accordionOpen}
          aria-controls={tableId}
          onClick={toggleAccordion}
        >
          <span className={styles.accordionTitle}>
            {t('compare.title')} · {t('compare.count', { count: state.variants.length })}
          </span>
          <svg
            className={`${styles.chevron} ${accordionOpen ? '' : styles.collapsed}`}
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.4}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      ) : (
        <h2 className={styles.heading}>{t('compare.title')}</h2>
      )}
      {showBody && totals.length < 2 && (
        <p className={styles.hint}>{t('compare.hint')}</p>
      )}

      {showBody && (
        <div className={styles.tableWrap} id={tableId}>
          <table className={styles.table}>
            <caption className="visually-hidden">{t('compare.caption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('compare.metric')}</th>
                {state.variants.map((v) => (
                  <th scope="col" key={v.id}>
                    <span className={styles.variantHeader}>
                      <span
                        className={styles.dot}
                        style={{ background: v.color }}
                        aria-hidden="true"
                      />
                      {v.name}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_METRICS.map((metric) => {
                const bestSet = computeBestSet(totals, metric);
                return (
                  <tr key={metric.key}>
                    <th scope="row">{t(metric.labelKey)}</th>
                    {totals.map((row) => {
                      const value = metric.getValue(row);
                      const formatted = metric.format(value, row, state.currencies, t);
                      const isBest = bestSet.has(row.variantId);
                      const isWarningsRow = metric.key === 'warnings';
                      const level = isWarningsRow ? maxWarningLevel(row) : null;
                      return (
                        <td
                          key={row.variantId}
                          className={isBest ? `mono ${styles.best}` : 'mono'}
                          aria-label={isBest ? t('compare.best', { value: formatted }) : undefined}
                          style={isWarningsRow ? { color: warningColorVar(level) } : undefined}
                        >
                          {isBest && (
                            <span className={styles.checkmark} aria-hidden="true">
                              ✓
                            </span>
                          )}
                          <span className={isWarningsRow ? styles.warnValue : undefined}>
                            {formatted}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Поделиться — единственное действие панели, поэтому кнопка крупная
          и во всю ширину: её ищут глазами, а не наводят курсор.
          Свёрнутая мобильная карточка её тоже прячет — делиться нечем, пока
          не видно даже таблицы. */}
      {showBody && <ShareButton variant="wide" />}
    </aside>
  );
}
